<script lang="ts" setup>
import type { CommandPaletteGroup, CommandPaletteItem } from '@nuxt/ui';
import type { UserAvatarUser } from '#layers/access/types/user-avatar';
import type { Filter, Uuid } from '#layers/data-model/public';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccessDisplayNames } from '#layers/access/composables/use-access-display-names';
import { useWorkspaceMemberSearch } from '#layers/access/composables/use-workspace-member-search';
import { useAuth } from '#layers/access/stores/auth';
import { CURRENT_USER_OPTION } from '#layers/data-model/public';
import UserAvatar from './UserAvatar.vue';
import UserAvatarIcon from './UserAvatarIcon.vue';

interface UserPickerProps {
	/** Marks the connected (multi) or active (single) member in the list. */
	isSelected: (member: UserAvatarUser) => boolean;
	/** Multi-select variant: rows show a checkbox and the popover stays open across selections. */
	multiple?: boolean;
	/** Filter narrowing the list (e.g. to omit members already connected when the picker opened). */
	filter?: Filter;
	/** Extra classes for the popover content panel, e.g. a fixed width. */
	contentClass?: string;
	/** Pin a "Current User" row above the members that emits the `$CURRENT_USER` variable when chosen. */
	allowCurrentUser?: boolean;
}

const { isSelected, multiple = false, filter, contentClass, allowCurrentUser = false } = defineProps<UserPickerProps>();

const emit = defineEmits<{ select: [memberId: Uuid] }>();

/** Identifies a synthetic picker entry (a token, not a real workspace member); extend for future entries like "No User". */
const UserPickerOptionKind = { CurrentUser: 'current-user' } as const;

const open = defineModel<boolean>('open', { default: false });

const FUSE_OPTIONS = { resultLimit: Number.POSITIVE_INFINITY };

const { t } = useI18n();
const { getUserDisplayName } = useAccessDisplayNames();
const { currentUser } = storeToRefs(useAuth());

const searchTerm = ref('');

const currentUserOption = computed(() => allowCurrentUser ? { id: CURRENT_USER_OPTION.id, label: t(CURRENT_USER_OPTION.labelKey) } : undefined);

const showCurrentUserOption = computed(() => Boolean(currentUserOption.value && currentUser.value && !searchTerm.value));

// The search text is owned here (needed for server-side search), so unlike a palette-internal
// term it survives the popover unmounting. Reset it on close so the next open starts unfiltered.
watch(open, (isOpen) => {
	if (!isOpen) searchTerm.value = '';
});

const { members, isLoading, error, hasMore, loadMore } = useWorkspaceMemberSearch({
	search: searchTerm,
	filter: () => filter,
});

// DCommandPopover's `groups` prop is fixed to the base CommandPaletteItem; extra per-row data
// (`member`) rides along via its `[key: string]: any` index signature, matching SpaceOrPageSelector.
const memberItems = computed<CommandPaletteItem[]>(() => members.value
	.map((member) => ({
		id: member.id,
		label: getUserDisplayName(member),
		member,
		onSelect: () => emit('select', member.id),
	})));

// The Current User row rides inside the members group rather than its own, so no group boundary
// renders between it and the real members.
const groups = computed<CommandPaletteGroup<CommandPaletteItem>[]>(() => {
	const option = currentUserOption.value;
	const items = [...memberItems.value];

	if (showCurrentUserOption.value && option) {
		items.unshift({
			id: option.id,
			label: option.label,
			kind: UserPickerOptionKind.CurrentUser,
			// Synthetic member so the shared `isSelected(member)` / checkbox path resolves by the sentinel id.
			member: { id: option.id, email: null, fullName: option.label },
			onSelect: () => emit('select', option.id),
		});
	}

	return [{
		id: 'members',
		// Search and exclusion are performed server-side; skip the client-side fuse filter.
		ignoreFilter: true,
		items,
	}];
});
</script>

<template>
	<DCommandPopover
		v-model:open="open"
		v-model:search-term="searchTerm"
		:groups
		:fuse="FUSE_OPTIONS"
		:loading="isLoading"
		:placeholder="t('common.search')"
		:popover="contentClass ? { ui: { content: contentClass } } : undefined"
		:can-load-more="hasMore"
		class="max-h-60"
		@load-more="loadMore"
	>
		<slot />

		<template #item="{ item: commandItem }">
			<div class="group/member-row flex w-full min-w-0 items-center gap-2">
				<DCheckbox
					v-if="multiple"
					:model-value="isSelected(commandItem.member)"
					class="pointer-events-none flex-none transition-opacity"
					color="neutral"
				/>
				<UserAvatarIcon
					v-if="commandItem.kind === UserPickerOptionKind.CurrentUser"
					icon="lucide:user"
					class="size-5 flex-none"
				/>
				<UserAvatar v-else :user="commandItem.member" class="size-5 flex-none" />
				<DTextOverflow class="text-xs font-medium text-default" :text="commandItem.label ?? ''" />
				<DTooltip v-if="commandItem.kind === UserPickerOptionKind.CurrentUser" :text="$t('filters.current_user_explanation')">
					<DIcon name="lucide:info" class="flex-none text-dimmed" />
				</DTooltip>
				<DIcon
					v-if="!multiple && isSelected(commandItem.member)"
					name="lucide:check"
					class="ms-auto size-4 flex-none text-muted"
				/>
			</div>
		</template>

		<template #empty>
			<span v-if="error" class="text-xs text-error">{{ $t('common.members_load_failed') }}</span>
			<span v-else class="text-xs text-dimmed">{{ $t('common.no_members_found') }}</span>
		</template>
	</DCommandPopover>
</template>
